<script setup>
import { computed, useSlots, useAttrs, inject } from 'vue'
import { convertSize } from 'core-ui/primeVueBridge/convertBvToPrime'

const slots = useSlots()
const attrs = useAttrs()

const props = defineProps({
  checked: null,
  modelValue: null,
  value: {
    default: true
  },
  uncheckedValue: {
    // Note: not applicable when multiple checkboxes bound to the same v-model array
    default: false
  },
  name: {
    type: String
  },
  indeterminate: {
    type: Boolean
  },
  disabled: {
    type: Boolean
  },
  size: {
    type: String,
    default: 'sm'
  },
  switch: {
    type: Boolean,
    default: false
  },
  binary: {}
})

const emit = defineEmits(['change', 'input', 'update:modelValue'])

const parentModel = inject('parentModel', null)
const setParentModel = inject('setParentModel', null)
const isInGroup = parentModel && setParentModel
const localModel = computed(() => {
  return isInGroup ? parentModel?.value : (props.modelValue ?? props.checked)
})

const isSwitch = computed(() => {
  return props.switch
})

const isBinary = computed(() => {
  return typeof props.binary !== 'undefined' ? props.binary : !Array.isArray(checkModel.value)
})

const inputId = computed(() => {
  return `checkbox-${Math.random().toString(36).slice(2)}`
})

const normalizedSize = computed(() => {
  return convertSize(props.size)
})

const checkModel = computed({
  get: () => localModel.value,
  set: (newVal, oldVal) => {
    if (isInGroup) {
      setParentModel(newVal)
    } else {
      emit('update:modelValue', newVal)
    }
  }
})

/** Need to account for when v-model is an array for p-toggle as
 * this isn't supported out of the box
 */
const switchModel = computed({
  get: () => {
    const model = localModel.value
    if (Array.isArray(model)) {
      const checked = [...model]
      const value = props.value
      return checked.includes(value) ? value : false
    }
    return model
  },
  set: (val) => {
    let adjustedVal = val
    const model = localModel.value

    if (Array.isArray(model)) {
      adjustedVal = convertSingleToArrayModel(val, model)
    }

    if (isInGroup) {
      setParentModel(adjustedVal)
    } else {
      emit('update:modelValue', adjustedVal)
    }
  }
})

const convertSingleToArrayModel = (val, model) => {
  const checked = [...model]
  const values = new Set(checked)
  if (val === props.value) {
    values.add(props.value)
  } else {
    values.delete(props.value)
  }
  return [...values]
}

const handleUpdate = ($event) => {
  let valToEmit = $event

  const model = props.modelValue ?? props.checked
  if (props.switch && Array.isArray(model)) {
    valToEmit = convertSingleToArrayModel($event, model)
  }
  emit('input', valToEmit)
  emit('change', valToEmit)
}

const commonProps = computed(() => ({
  inputId: inputId.value,
  name: props.name,
  disabled: props.disabled,
  size: normalizedSize.value,
  value: props.value,
  'true-value': props.hasOwnProperty('value') ? props.value : props.name,
  'false-value': props.uncheckedValue,

  // accessibility
  'aria-describedby': attrs['aria-describedby'],
  'aria-labelledby': attrs['aria-labelledby'],

  // event listeners
  'onUpdate:modelValue': handleUpdate
}))

const switchDt = {
  width: '28px',
  height: '16px',
  borderRadius: '10px',
  gap: '2px',
  handle: {
    size: '12px',
    disabled: {
      background: '{surface.0}'
    }
  },
  background: '{primary.200}',
  hover: {
    background: '{primary.300}'
  },
  disabled: {
    background: '{surface.100}'
  }
}

const checkDt = {
  disabled: {
    background: '{surface.100}'
  }
}
</script>

<template>
  <div class="d-flex gap-2">
    <p-toggle-switch
      v-if="isSwitch"
      v-model="switchModel"
      v-bind="commonProps"
      class="mt-1"
      :dt="switchDt"
      :pt="{ root: 'flex-shrink-0' }"
    />

    <p-checkbox
      v-else
      v-model="checkModel"
      v-bind="commonProps"
      class="mt-1"
      :binary="isBinary"
      :indeterminate="indeterminate"
      :dt="checkDt"
    />

    <label
      v-if="slots.default"
      :for="inputId"
      class="ml-3 mb-0"
      :class="disabled ? 'text-muted' : ''"
    >
      <slot name="default"></slot>
    </label>
  </div>
</template>
